import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { API } from "../config/api";
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Check, Loader2, Shield, FileText, AlertCircle, Undo2, HelpCircle,
} from "lucide-react";
import { LOGO_URL } from "../config/constants";
import CmraQuestions, {
  initialCmraState, cmraFirstProblem, checklistPayload,
} from "../components/risk/CmraQuestions";
import { openMatterCcl } from "../utils/openMatterCcl";
import { openConflictCheck } from "../utils/openConflictCheck";
import NextStepBanner from "../components/NextStepBanner";
import { clientDisplayName, jointNamesFrom } from "../utils/clientDisplayName";

/**
 * FORF Combined workflow — locked-down 29 Apr 2026.
 *
 * Section 1: cascading practice-area → specific-matter-type, universal
 *   commercial-terms fields, per-matter-type conditional fields driven by
 *   the CCL template's placeholder set, and tooltips on every field.
 * Section 2 (BM-H593, 23 Sep 2026): the one client and matter risk
 *   assessment (CMRA). Same questions as the old matter-page panel
 *   (8 factors, LSAG 5-category checklist, 80-char narrative,
 *   mitigations) via the shared components/risk/CmraQuestions, plus
 *   key deadline, review date, material risks and supervisor notes.
 *   If an assessment is already on file the fee earner can keep it
 *   (Continue to CCL) or redo it (appends a new CMRA history row).
 * Both sections produce a PDF filed to Compliance & Financials, then the
 * pipeline advances to CCL Draft.
 */

const CLIENT_TYPES = [
  { value: "individual", label: "Individual" },
  { value: "company",    label: "Company" },
  { value: "llp",        label: "LLP" },
];

const PRICING_MODELS = ["Fixed fee", "Hourly", "Blended"];
// Fee Split (Q15) was dropped from the FORF surface in the 29Apr2026
// cleanup PR. Practice area is set at the conflict-check stage (Stage 1)
// and is read-only here. Pro-forma scope no longer requires a free-text
// scope-of-work entry on top of the template.

// BM-H593: the 8-factor list, LSAG checklist and 80-character narrative
// rule live in components/risk/CmraQuestions.jsx so the FORF and every
// other screen ask exactly the same questions.

const TOOLTIPS = {
  client_type: "Pick the legal entity. Wrong choice triggers OB hard-stop.",
  specific_matter_type: "Drives the CCL template. If the right one isn't here, pick General — don't pick the closest.",
  pro_forma_scope: "Yes if this matter type has a stock CCL scope clause. No if scope must be drafted bespoke.",
  scope_of_work: "What the client has instructed you to do. Specific. Will be the basis of the CCL scope clause. Aim for 3-6 sentences. e.g. 'Advising on and drafting a shareholders agreement for X Ltd, including buyback provisions and pre-emption rights on share transfers.'",
  scope_exclusions: "Standard exclusions go here so the client knows what's outside the retainer. Bullet list welcome. e.g. tax advice; employment matters; disputes; cross-border issues; SDLT advice.",
  Q13: "Fixed fee, hourly, or blended. Drives the CCL fee clause.",
  Q14: "£ amount or range. Drives the CCL fee section and Lawbillity.",
  Q16: "What the client must pay externally — Land Registry, search fees, court fees etc.",
  Q17: "Yes if the client must pay money on account. Enter the amount as legal fees before VAT. The CCL and its covering email then ask the client for it, showing the total with VAT. It does not hold the matter up.",
};

function getUserEmail() {
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const u = JSON.parse(userStr);
      if (u.email) return u.email;
    }
  } catch (e) {}
  return (
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") ||
    ""
  );
}

function Tip({ text, id }) {
  if (!text) return null;
  return (
    <span
      className="inline-flex items-center ml-1 text-slate-400 hover:text-teal cursor-help align-middle"
      tabIndex={0}
      role="tooltip"
      aria-label={text}
      title={text}
      data-testid={id ? `tooltip-${id}` : undefined}
    >
      <HelpCircle className="w-3.5 h-3.5" />
    </span>
  );
}

function FieldRow({ label, value, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <div
        className="mt-1 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm min-h-[36px]"
        data-testid={testid}
      >
        {value || <span className="text-slate-400 italic">Not set</span>}
      </div>
    </div>
  );
}

// BM-H599: a per-page-load random suffix so browser autofill can never
// match a FORF field to a value typed for another client on another form.
function useFieldSalt() {
  const ref = useRef(null);
  if (ref.current === null) {
    ref.current = Math.random().toString(36).slice(2, 10);
  }
  return ref.current;
}

function EditableTextField({ label, fieldKey, value, onChange, placeholder,
                            tooltip, testid, required, type = "text", maxLength }) {
  const salt = useFieldSalt();
  return (
    <div>
      <Label htmlFor={testid} className="text-xs text-slate-500">
        {label}{required && <span className="text-red-500"> *</span>}
        <Tip text={tooltip} id={fieldKey} />
      </Label>
      <Input
        id={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="mt-1"
        data-testid={testid}
        name={`forf-${fieldKey || "field"}-${salt}`}
        autoComplete="off"
      />
    </div>
  );
}

/**
 * BM-H599 (data protection): client email, phone and address come from
 * the onboarding (OB) form and are shown locked. Changing one needs a
 * deliberate "Change", a new value, then an explicit confirm. The
 * backend refuses a Section 1 submit whose contact details differ from
 * the OB without client_contact_change_confirmed: true, and logs the
 * change (old, new, who, when).
 */
function LockedContactField({ label, fieldKey, value, obValue, sourceRef, onConfirmChange, testid }) {
  const salt = useFieldSalt();
  const [mode, setMode] = useState("locked"); // locked | editing | confirming
  const [draft, setDraft] = useState("");
  const inputId = `${testid}-${salt}`;
  const changed = (value || "") !== (obValue || "");
  return (
    <div data-testid={`${testid}-block`}>
      <Label htmlFor={inputId} className="text-xs text-slate-500">
        {label}
        <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">
          {changed
            ? "changed on this FORF"
            : sourceRef
              ? `from linked matter ${sourceRef}`
              : "from onboarding"}
        </span>
      </Label>
      {mode === "locked" && (
        <div className="mt-1 flex items-center gap-2">
          <div
            className="flex-1 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm min-h-[36px]"
            data-testid={testid}
            aria-readonly="true"
          >
            {value || <span className="text-slate-400">Not provided at onboarding</span>}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => { setDraft(value || ""); setMode("editing"); }}
            data-testid={`${testid}-change`}
          >
            Change
          </Button>
        </div>
      )}
      {mode !== "locked" && (
        <div className="mt-1 space-y-2">
          <Input
            id={inputId}
            name={`forf-${fieldKey}-${salt}`}
            autoComplete="off"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={mode === "confirming"}
            data-testid={`${testid}-edit`}
          />
          {mode === "editing" && (
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => setMode("confirming")}
                disabled={draft.trim() === (value || "").trim()}
                data-testid={`${testid}-save`}
              >
                Save change
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setMode("locked")}
                      data-testid={`${testid}-cancel`}>
                Cancel
              </Button>
            </div>
          )}
          {mode === "confirming" && (
            <div className="p-3 rounded-md border border-amber-300 bg-amber-50 text-sm text-amber-800"
                 data-testid={`${testid}-confirm-panel`}>
              <p className="mb-2">
                Change the client's {label.toLowerCase()} from "{value || "(blank)"}" to "{draft.trim()}"?
                This updates the contact details held for this client and is recorded on the audit trail.
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => { onConfirmChange(draft.trim()); setMode("locked"); }}
                  data-testid={`${testid}-confirm`}
                >
                  Yes, change it
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setMode("editing")}
                        data-testid={`${testid}-confirm-cancel`}>
                  Go back
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FoRFCombined() {
  const navigate = useNavigate();
  const { matterRef } = useParams();
  const [searchParams] = useSearchParams();
  const wantRiskStep = searchParams.get("step") === "risk";
  const riskStepApplied = useRef(false);
  const [step, setStep] = useState("section-1");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [conflictsGate, setConflictsGate] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [section1, setSection1] = useState({});
  const [matterSpecific, setMatterSpecific] = useState({});
  const [taxonomy, setTaxonomy] = useState(null);
  // Section 2 (BM-H593): the CMRA answers live in `cmra` (shared
  // CmraQuestions shape); `raData` keeps the four FORF-only extras.
  const [cmra, setCmra] = useState(initialCmraState);
  const [cmraSubmitAttempted, setCmraSubmitAttempted] = useState(false);
  const [raData, setRaData] = useState({
    material_risks: "",
    key_deadline: "",
    supervisor_notes: "",
    review_date: "",
  });
  const [onFile, setOnFile] = useState(null);
  const [redoMode, setRedoMode] = useState(false);
  const [pipelineWarning, setPipelineWarning] = useState("");
  // BM-H622: whether a High risk assessment reached the MLCO queue.
  const [mlcoRouting, setMlcoRouting] = useState(null);
  const [contactConfirmed, setContactConfirmed] = useState(false);
  const [openingCcl, setOpeningCcl] = useState(false);
  const [forfStatus, setForfStatus] = useState("");
  const [revertOpen, setRevertOpen] = useState(false);
  const [reverting, setReverting] = useState(false);

  const loadPrefill = async () => {
    setLoading(true);
    setErr("");
    try {
      const [pre, taxon] = await Promise.all([
        axios.get(`${API}/forf/${encodeURIComponent(matterRef)}/prefill`),
        axios.get(`${API}/config/matter-types`),
      ]);
      // P0 HOTFIX (29Apr2026): hard-stop if the server returned a prefill
      // for a different matter than the URL says. Belt-and-braces — the
      // backend now refuses to serve a mismatch, but the FE must never
      // render the wrong client's data.
      const servedRef = (pre.data?.matter_reference || "").trim();
      const prefillRef = (pre.data?.prefill?.matter_reference || "").trim();
      if (
        (servedRef && servedRef !== matterRef) ||
        (prefillRef && prefillRef !== matterRef)
      ) {
        setPrefill(null);
        setSection1({});
        setMatterSpecific({});
        setTaxonomy(null);
        setForfStatus("");
        setErr(
          `Refusing to display FORF: server returned matter ${servedRef || prefillRef} ` +
          `for URL ${matterRef}. Please refresh and re-open from the matter page.`
        );
        return;
      }
      setPrefill(pre.data.prefill);
      setSection1(pre.data.prefill || {});
      setMatterSpecific((pre.data.prefill || {}).matter_specific || {});
      setForfStatus(pre.data.forf_status || "");
      setOnFile(pre.data.risk_assessment_on_file || null);
      setContactConfirmed(false);
      setTaxonomy(taxon.data);
      // BM-H593: /forf/{ref}?step=risk (the matter page's red button)
      // opens at Section 2 when Section 1 is already submitted.
      const st = pre.data.forf_status || "";
      if (wantRiskStep && !riskStepApplied.current &&
          (st === "section_1_submitted" || st === "section_2_submitted")) {
        riskStepApplied.current = true;
        setStep("section-2");
      }
      // Default review date = today + 90 days if empty.
      setRaData((d) => ({
        ...d,
        review_date: d.review_date || (() => {
          const t = new Date(); t.setDate(t.getDate() + 90);
          return t.toISOString().slice(0, 10);
        })(),
      }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      // Stage 1 gate (29Apr2026): server returns 409 with
      // error="forf_blocked_conflicts_incomplete" when the conflicts
      // check has not been completed for this matter. Render a dedicated
      // block panel with a link back to the conflict-check screen rather
      // than treating this as a generic load error.
      if (status === 409 && detail && typeof detail === "object" &&
          detail.error === "forf_blocked_conflicts_incomplete") {
        setConflictsGate({
          matterRef: detail.matter_reference || matterRef,
          conflictStatus: detail.conflict_status || "",
          conflictCheckPresent: !!detail.conflict_check_present,
          conflictCheckId: detail.conflict_check_id || "",
          waitingOn: detail.waiting_on || "",
          statusPlain: detail.status_plain || "",
          message: detail.message || "Complete conflicts check before opening FORF.",
        });
        return;
      }
      if (detail && typeof detail === "object" && detail.error === "forf_cross_matter_mismatch_blocked") {
        setErr(
          `Cross-matter routing blocked: server refused to serve another ` +
          `matter's FORF (requested ${detail.requested_matter_ref}, would have ` +
          `served ${detail.served_matter_ref}). Please contact support.`
        );
      } else {
        setErr(errorMessage(e, "Failed to load FORF prefill"));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!matterRef) return;
    // P0 HOTFIX (29Apr2026): clear any stale state from a previous matter
    // navigation BEFORE fetching the new prefill. If the user navigates
    // from matter A's FORF to matter B's FORF, the in-flight render must
    // never display A's data while B's prefill is loading.
    setPrefill(null);
    setSection1({});
    setMatterSpecific({});
    setTaxonomy(null);
    setForfStatus("");
    setStep("section-1");
    setErr("");
    setConflictsGate(null);
    loadPrefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterRef]);

  const isSubmittedStatus = forfStatus && forfStatus !== "draft";

  const confirmRevertToDraft = async () => {
    setReverting(true);
    try {
      const userId =
        sessionStorage.getItem("bonsai_user_id") ||
        localStorage.getItem("bonsai_user_id") ||
        getUserEmail();
      await axios.post(
        `${API}/forf/${encodeURIComponent(matterRef)}/reopen`,
        { reason: "Reverted to draft via FORF page" },
        { headers: { "X-Bonsai-User-Id": userId || "" } },
      );
      setRevertOpen(false);
      toast.success("FORF reverted to draft. All fields are now editable.");
      setStep("section-1");
      await loadPrefill();
    } catch (e) {
      toast.error(errorMessage(e, "Failed to revert FORF to draft"));
    } finally {
      setReverting(false);
    }
  };

  const update1 = (key, value) => setSection1((s) => ({ ...s, [key]: value }));
  const updateMS = (key, value) => setMatterSpecific((m) => ({ ...m, [key]: value }));
  const confirmContactChange = (key, value) => {
    setSection1((s) => ({ ...s, [key]: value }));
    setContactConfirmed(true);
  };
  const onCmraChange = useCallback(
    (patch) => setCmra((c) => ({ ...c, ...patch })),
    [],
  );
  const obContact = (prefill && prefill.ob_contact) || {};
  // BM-H3280: contact details taken from the matter this one is linked to.
  const obContactSource = (prefill && prefill.ob_contact_source) || {};
  const linkedContactRefused = (prefill && prefill.linked_contact_refused) || "";
  // BM-H3305: a director or the MLCO confirms two differently written names
  // are the same client, and the linked contact details are carried across.
  const [samePersonOpen, setSamePersonOpen] = useState(false);
  const [samePersonTick, setSamePersonTick] = useState(false);
  const [samePersonReason, setSamePersonReason] = useState("");
  const [samePersonBusy, setSamePersonBusy] = useState(false);
  const [samePersonMsg, setSamePersonMsg] = useState("");
  const confirmSamePerson = async () => {
    setSamePersonBusy(true);
    setSamePersonMsg("");
    try {
      const resp = await axios.post(
        `${API}/forf/${encodeURIComponent(matterRef)}/confirm-linked-contact`,
        { confirm_same_person: samePersonTick, reason: samePersonReason },
      );
      const pre = await axios.get(`${API}/forf/${encodeURIComponent(matterRef)}/prefill`);
      const fresh = pre.data?.prefill || {};
      if ((pre.data?.matter_reference || "").trim() === matterRef) {
        setPrefill(fresh);
        setSection1((s) => ({
          ...s,
          client_email: fresh.client_email || s.client_email,
          client_phone: fresh.client_phone || s.client_phone,
          client_address: fresh.client_address || s.client_address,
        }));
      }
      const carried = resp.data?.fields_carried || [];
      setSamePersonMsg(carried.length
        ? `Confirmed. Carried across: ${carried.filter((f) => f !== "entity_type").join(", ") || "client type"}.`
        : (resp.data?.skip_reason || "Confirmed. There was nothing blank to fill."));
      setSamePersonOpen(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setSamePersonMsg((d && d.message) || "Could not confirm. Nothing has been changed.");
    } finally {
      setSamePersonBusy(false);
    }
  };

  // Refresh status + "already on file" after a submit without a
  // full-page reload (BM-H598: after a revert and Section 1 resubmit the
  // fee earner lands on Section 2 showing the assessment on file).
  const refreshStatus = async () => {
    try {
      const pre = await axios.get(`${API}/forf/${encodeURIComponent(matterRef)}/prefill`);
      const servedRef = (pre.data?.matter_reference || "").trim();
      if (servedRef && servedRef !== matterRef) return;
      setForfStatus(pre.data.forf_status || "");
      setOnFile(pre.data.risk_assessment_on_file || null);
    } catch (e) {
      // Non-fatal: Section 2 still works without the on-file summary.
    }
  };

  // Cascading dropdowns: specific options for current practice area.
  const practiceAreas = taxonomy?.practice_areas || [];
  const currentSpecifics = useMemo(() => {
    if (!taxonomy) return [];
    const pa = section1.matter_type;
    return (taxonomy.specific_matter_types || {})[pa] || [];
  }, [taxonomy, section1.matter_type]);
  const currentSpecificEntry = useMemo(
    () => currentSpecifics.find((s) => s.code === section1.specific_matter_type) || null,
    [currentSpecifics, section1.specific_matter_type],
  );
  const requiredFields = currentSpecificEntry?.required_forf_fields || [];

  const submitSection1 = async () => {
    setErr("");
    setSubmitting(true);
    try {
      // Brief 67: request_new intercept — POST to dedicated endpoint,
      // do NOT advance to section-2 (matter is parked pending Vanessa's action).
      if (section1.specific_matter_type === "request_new") {
        const resp = await axios.post(
          `${API}/forf/request-new-matter-type`,
          {
            submission_id: matterRef,
            practice_area: section1.matter_type,
            requested_matter_label: matterSpecific.requested_matter_label || "",
            requested_matter_description: matterSpecific.requested_matter_description || "",
            requested_urgency: matterSpecific.requested_urgency || "Standard",
          },
          { headers: { "X-User-Email": getUserEmail() } },
        );
        toast.success("Request sent to Vanessa. She\u2019ll be in touch shortly.");
        void resp;
        return;  // Do not advance to section-2
      }

      // 29Apr2026 cleanup pt2 (Vanessa): tob_accepted / tob_version /
      // Q8_obligation are removed from the FORF surface. Strip them
      // from the section-1 payload AND from matter_specific so an old
      // draft that still carries them doesn't trigger the BE 400 reject.
      const { tob_accepted: _ta, tob_version: _tv, ob_contact: _oc,
              ob_contact_source: _ocs, linked_contact_refused: _lcr,
              field_sources: _fs,
              client_contact_change_confirmed: _cc,
              ...section1Clean } = section1 || {};
      const { Q8_obligation: _qo, obligation: _ob,
              ...matterSpecificClean } = matterSpecific || {};
      const payload = {
        section_1_data: {
          ...section1Clean,
          client_type: (section1Clean.client_type || "").toLowerCase(),
          matter_specific: matterSpecificClean,
        },
        // BM-H599: only true after the fee earner pressed Change and
        // confirmed a new client email / phone / address.
        client_contact_change_confirmed: contactConfirmed,
      };
      const resp = await axios.post(
        `${API}/forf/${encodeURIComponent(matterRef)}/section-1`,
        payload,
        { headers: { "X-User-Email": getUserEmail() } },
      );
      toast.success("FORF section 1 submitted. Generating the FORF PDF.");
      void resp;
      setContactConfirmed(false);
      setRedoMode(false);
      await refreshStatus();
      setStep("section-2");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg;
      if (detail && typeof detail === "object") {
        // SharePoint-upload-failed: the backend has already saved your
        // entries as a draft (draft_saved / recoverable), so tell the
        // user to fix the folder issue and try again — do NOT emit a
        // trailing '(field)' fragment when no field key applies.
        if (detail.error === "client_contact_change_unconfirmed") {
          msg = detail.message ||
            "The client's contact details differ from onboarding. Use Change and confirm before submitting.";
        } else if (detail.error === "sharepoint_upload_failed") {
          const parts = [detail.message || "FORF PDF upload failed."];
          if (detail.retry_hint) parts.push(detail.retry_hint);
          if (detail.draft_saved) {
            parts.push(
              "Your section-1 entries are preserved on the draft — close and " +
              "re-open FORF section 1 to see them, then press Submit again."
            );
          } else {
            parts.push(
              "WARNING: your entries could NOT be preserved on the draft; " +
              "do not close the tab until this is resolved."
            );
          }
          msg = parts.join(" ");
        } else if (detail.field) {
          msg = `${detail.message || "Section 1 invalid"} (${detail.field})`;
        } else {
          msg = detail.message || detail.error || "Section 1 invalid";
        }
      } else {
        msg = errorMessage(e, "Section 1 submit failed");
      }
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const submitSection2 = async () => {
    setErr("");
    setCmraSubmitAttempted(true);
    // Client-side gate mirrors the server (it is authoritative): all 8
    // factors rated, every LSAG category addressed or N/A with a reason,
    // narrative at least 80 characters, mitigations at Medium/High.
    const problem = cmraFirstProblem(cmra);
    if (problem) {
      setErr(problem);
      toast.error(problem);
      return;
    }
    if (!(raData.review_date || "").trim()) {
      const msg = "Review date is required.";
      setErr(msg);
      toast.error(msg);
      return;
    }
    setSubmitting(true);
    try {
      // Flat 8-factor answers + CMRA fields. We deliberately do NOT
      // send the band or score: the server computes them.
      const ra_payload = {
        ...(cmra.riskFactors || {}),
        risk_narrative: (cmra.narrative || "").trim(),
        mitigating_actions: (cmra.mitigations || "").trim(),
        risk_factors_considered: checklistPayload(cmra.checklist),
        material_risks: raData.material_risks,
        key_deadline: raData.key_deadline,
        review_date: raData.review_date,
        supervisor_notes: raData.supervisor_notes,
        client_name: section1.client_name,
        matter_reference: matterRef,
      };
      const redo = redoMode || forfStatus === "section_2_submitted";
      const resp = await axios.post(
        `${API}/forf/${encodeURIComponent(matterRef)}/section-2`,
        { risk_assessment: ra_payload, redo },
        { headers: { "X-User-Email": getUserEmail() } },
      );
      setPipelineWarning(resp?.data?.pipeline_error || "");
      setMlcoRouting(resp?.data?.mlco_routing || null);
      setForfStatus("section_2_submitted");
      toast.success("Risk assessment filed");
      setStep("done");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object")
        ? (detail.message || JSON.stringify(detail))
        : errorMessage(e, "Section 2 submit failed");
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // BM-H593 / BM-H598: keep the risk assessment already on file.
  const continueWithOnFile = async () => {
    setErr("");
    if (forfStatus === "section_2_submitted") {
      setPipelineWarning("");
      setStep("done");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await axios.post(
        `${API}/forf/${encodeURIComponent(matterRef)}/section-2`,
        { use_on_file_assessment: true },
        { headers: { "X-User-Email": getUserEmail() } },
      );
      setPipelineWarning(resp?.data?.pipeline_error || "");
      setForfStatus("section_2_submitted");
      toast.success("Existing risk assessment kept");
      setStep("done");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object")
        ? (detail.message || JSON.stringify(detail))
        : errorMessage(e, "Could not continue with the risk assessment on file");
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const openDraftCcl = async () => {
    setOpeningCcl(true);
    try {
      await openMatterCcl({
        matterRef,
        axios,
        apiBase: API,
        navigate,
        onError: (msg) => toast.error(msg),
      });
    } finally {
      setOpeningCcl(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0]">
        <Loader2 className="w-8 h-8 animate-spin text-teal" />
      </div>
    );
  }

  if (conflictsGate) {
    // BM-H3330: this button used to build /conflict-check/{matter ref},
    // which the conflict page cannot find (it looks checks up by id).
    // openConflictCheck opens the check itself, or the approval page for
    // the director it is waiting on, as the Pipeline Tracker does (BM-H3263).
    const goToConflictCheck = () => openConflictCheck({
      conflictCheckId: conflictsGate.conflictCheckId,
      matterRef: conflictsGate.matterRef,
      axios,
      apiBase: API,
      navigate,
      onError: (msg) => toast.error(msg),
    });
    const hasCheck = conflictsGate.conflictCheckPresent;
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-6">
        <div
          className="max-w-xl bg-white border border-amber-300 rounded-md p-6 text-center"
          data-testid="forf-conflicts-gate"
        >
          <Shield className="w-8 h-8 text-amber-600 mx-auto mb-3" />
          <h2 className="text-base font-semibold text-amber-800 mb-1">
            You can't start the FORF yet: the conflict check hasn't been cleared
          </h2>
          <p className="text-sm text-amber-800 mb-2" data-testid="forf-conflicts-gate-message">
            {conflictsGate.statusPlain || conflictsGate.message}
          </p>
          {conflictsGate.waitingOn && (
            <p className="text-sm text-amber-800 mb-2" data-testid="forf-conflicts-gate-waiting-on">
              Waiting on: <span className="font-medium">{conflictsGate.waitingOn}</span>
            </p>
          )}
          <p className="text-xs text-amber-700 mb-4">
            Matter <span className="font-mono">{conflictsGate.matterRef}</span>
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={() => navigate(-1)} data-testid="forf-conflicts-gate-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button
              className="bg-teal hover:bg-teal/90 text-white"
              onClick={goToConflictCheck}
              data-testid="forf-conflicts-gate-link"
            >
              {hasCheck ? "Open the conflict check" : "Start a conflict check"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (err && !prefill) {
    const isNotFound = typeof err === "string" && err.trim().toLowerCase() === "not found";
    const friendly = "This matter isn't loading. If the matter reference is correct, please ping the matter owner or an admin. If you think this is a bug, screenshot this page and message Vanessa.";
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-6">
        <div className="max-w-xl bg-white border border-red-200 rounded-md p-6 text-center" data-testid="forf-error">
          <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-3" />
          <p className="text-sm text-red-700 mb-4" data-testid="forf-error-message">{isNotFound ? friendly : err}</p>
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="forf-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <nav className="sticky top-0 z-40 w-full border-b bg-white/95 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Bonsai Law" className="w-10 h-10" />
            <div>
              <h1 className="text-lg font-bold text-teal" data-testid="forf-page-title">
                FORF: File Opening Request Form
              </h1>
              <p className="text-xs text-slate-500 font-mono">{matterRef}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSubmittedStatus && (
              <Button
                variant="outline"
                onClick={() => setRevertOpen(true)}
                data-testid="forf-revert-to-draft-btn"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <Undo2 className="w-4 h-4 mr-2" /> Revert to Draft
              </Button>
            )}
            <Button variant="ghost" onClick={() => navigate(-1)} data-testid="forf-nav-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </div>
        </div>
      </nav>

      <AlertDialog open={revertOpen} onOpenChange={setRevertOpen}>
        <AlertDialogContent data-testid="forf-revert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revert this FORF to draft?</AlertDialogTitle>
            <AlertDialogDescription>
              This will flip the FORF back to draft so all fields can be edited.
              Section 2 progress will be rewound and the risk assessment will be
              superseded. An audit entry is recorded with your name. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reverting} data-testid="forf-revert-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRevertToDraft}
              disabled={reverting}
              className="bg-status-red text-white hover:bg-status-red/90"
              data-testid="forf-revert-confirm"
            >
              {reverting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
              Revert to Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* BM-H3368: where this matter is in the whole flow, and what's next. */}
        <NextStepBanner matterRef={matterRef} light refreshKey={step} />
        {/* Stepper */}
        <ol className="flex items-center justify-center gap-4 mb-10" data-testid="forf-stepper">
          {[
            { id: "section-1", label: "Section 1 — FORF", icon: FileText },
            { id: "section-2", label: "Section 2 — Risk Assessment", icon: Shield },
            { id: "done",      label: "Filed", icon: Check },
          ].map((s, i) => {
            const active = step === s.id;
            const complete = (step === "section-2" && s.id === "section-1")
                          || (step === "done" && s.id !== "done");
            const Icon = s.icon;
            return (
              <li key={s.id} className="flex items-center gap-2">
                <div
                  data-testid={`forf-step-${s.id}`}
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    active ? "bg-teal text-white" :
                    complete ? "bg-teal-dim text-teal" :
                    "bg-slate-200 text-slate-500"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <span className={`text-sm ${active ? "font-semibold text-teal" : "text-slate-500"}`}>
                  {s.label}
                </span>
                {i < 2 && <span className="w-8 h-0.5 bg-slate-200" />}
              </li>
            );
          })}
        </ol>

        {/* ─────── Section 1 ─────── */}
        {step === "section-1" && (
          <section className="bg-white border border-teal-border rounded-md p-6 shadow-sm" data-testid="forf-section-1">
            <h2 className="text-base font-semibold text-teal mb-1">Section 1 — FORF</h2>
            <p className="text-xs text-slate-500 mb-4">
              Identity fields are pre-populated from the conflict check and
              onboarding stages — confirm or correct them. Pick a specific
              matter type so the right CCL template is used. Commercial terms
              are required to generate the CCL fee section. Hover the (?)
              icons for guidance.
            </p>

            {err && (
              <div className="mb-4 p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-700" data-testid="forf-section-1-error">
                {err}
              </div>
            )}

            {/* Identity */}
            <h3 className="text-sm font-semibold text-teal mt-2 mb-2">Identity & matter</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <FieldRow label="Matter reference" value={section1.matter_reference} testid="prefill-matter-reference" />
              <FieldRow label={jointNamesFrom(section1.joint_clients).length ? "Clients" : "Client name"} value={clientDisplayName(section1)} testid="prefill-client-name" />

              <div data-testid="prefill-client-type">
                <Label className="text-xs text-slate-500">
                  Client type *<Tip text={TOOLTIPS.client_type} id="client_type" />
                </Label>
                <Select
                  value={(section1.client_type || "").toLowerCase()}
                  onValueChange={(v) => update1("client_type", v)}
                >
                  <SelectTrigger className="mt-1" data-testid="forf-client-type-select">
                    <SelectValue placeholder="Select client type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIENT_TYPES.map((c) => (
                      <SelectItem key={c.value} value={c.value} data-testid={`forf-client-type-${c.value}`}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <FieldRow
                label={(prefill?.field_sources || {}).fee_earner === "conflict_check"
                  ? "Fee earner (from the conflict check)" : "Fee earner"}
                value={section1.fee_earner}
                testid="prefill-fee-earner"
              />

              {/* PR O (21May2026): Capacity note from OB. Read-only — the
                  client said on OB that they are acting on behalf of
                  someone else and gave brief details. Surfaces here so the
                  fee earner sees it without having to dig into the OB
                  record or chase the client by email. Only renders when
                  the OB flag is explicitly "no". */}
              {((section1.acting_for_self || "").toLowerCase() === "no") && (
                <div className="md:col-span-2 p-3 rounded-md border border-amber-700/50 bg-amber-900/20" data-testid="forf-capacity-note">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <Label className="text-xs text-amber-300 font-semibold uppercase tracking-wider">Capacity note (from OB)</Label>
                      <p className="text-xs text-slate-400 mt-1">Client confirmed at onboarding that they are NOT acting solely in their own name.</p>
                      {section1.acting_for_self_details && (
                        <p className="text-sm text-amber-100 mt-2 whitespace-pre-wrap" data-testid="forf-capacity-note-details">{section1.acting_for_self_details}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* BM-H599: client contact details come from onboarding and
                  are locked. A change needs Change, a new value, and an
                  explicit confirm; the backend audits it. */}
              <LockedContactField
                label="Client email" fieldKey="client_email" testid="forf-client-email-input"
                value={section1.client_email || ""}
                obValue={obContact.client_email || ""}
                sourceRef={obContactSource.client_email || ""}
                onConfirmChange={(v) => confirmContactChange("client_email", v)}
              />
              <LockedContactField
                label="Client phone" fieldKey="client_phone" testid="forf-client-phone-input"
                value={section1.client_phone || ""}
                obValue={obContact.client_phone || ""}
                sourceRef={obContactSource.client_phone || ""}
                onConfirmChange={(v) => confirmContactChange("client_phone", v)}
              />
              <div className="md:col-span-2">
                <LockedContactField
                  label="Client address" fieldKey="client_address" testid="forf-client-address-input"
                  value={section1.client_address || ""}
                  obValue={obContact.client_address || ""}
                  sourceRef={obContactSource.client_address || ""}
                  onConfirmChange={(v) => confirmContactChange("client_address", v)}
                />
              </div>
              {linkedContactRefused && (
                <div className="md:col-span-2 space-y-2" data-testid="forf-linked-contact-refused-block">
                  <p className="text-xs text-amber-300" data-testid="forf-linked-contact-refused">
                    {linkedContactRefused}
                  </p>
                  {!samePersonOpen && (
                    <Button type="button" size="sm" variant="outline"
                            onClick={() => { setSamePersonOpen(true); setSamePersonMsg(""); }}
                            data-testid="forf-same-person-open">
                      Same person? Confirm
                    </Button>
                  )}
                  {samePersonOpen && (
                    <div className="rounded-md border border-slate-600 p-3 space-y-2">
                      <p className="text-xs text-slate-300">
                        A director or the MLCO can confirm this. The client's email, phone and
                        address are then copied from {prefill?.linked_contact_source_ref || "the linked matter"} into
                        any blank fields here. Your name and reason are recorded on the file.
                      </p>
                      <label className="flex items-start gap-2 text-sm">
                        <input type="checkbox" checked={samePersonTick}
                               onChange={(e) => setSamePersonTick(e.target.checked)}
                               data-testid="forf-same-person-tick" />
                        <span>I confirm these two names are the same client.</span>
                      </label>
                      <Textarea value={samePersonReason}
                                onChange={(e) => setSamePersonReason(e.target.value)}
                                placeholder="Why you are satisfied it is the same person (at least 20 characters)"
                                data-testid="forf-same-person-reason" />
                      <div className="flex gap-2">
                        <Button type="button" size="sm"
                                disabled={samePersonBusy || !samePersonTick || samePersonReason.trim().length < 20}
                                onClick={confirmSamePerson}
                                data-testid="forf-same-person-confirm">
                          {samePersonBusy ? "Confirming…" : "Confirm same person"}
                        </Button>
                        <Button type="button" size="sm" variant="ghost"
                                onClick={() => setSamePersonOpen(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {samePersonMsg && (
                <p className="md:col-span-2 text-xs text-slate-300" data-testid="forf-same-person-msg">
                  {samePersonMsg}
                </p>
              )}

              {/* Practice area: editable on the FORF surface (Apr 2026
                  fix). The conflict check is no longer a hard gate for
                  this field — when conflict_checks.practice_area is empty
                  the fee earner can set it here and the FORF submit
                  endpoint writes it back to matter_tracker (and to
                  conflict_checks if the row exists) so downstream CCL
                  template selection works. Required to submit. */}
              <div data-testid="prefill-matter-type">
                <Label className="text-xs text-slate-500">
                  Practice area *<Tip text="Drives CCL template selection. Required." id="practice_area" />
                </Label>
                <Select
                  value={section1.matter_type || ""}
                  onValueChange={(v) => {
                    // Reset specific_matter_type when practice area changes
                    // so the cascading dropdown doesn't keep a stale code
                    // that no longer exists under the new practice area.
                    setSection1((s) => ({
                      ...s,
                      matter_type: v,
                      specific_matter_type: s.matter_type === v ? s.specific_matter_type : "",
                    }));
                  }}
                >
                  <SelectTrigger className="mt-1" data-testid="forf-matter-type-select">
                    <SelectValue placeholder="Select practice area" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Taxonomy-driven. The hardcoded four-entry list that
                        used to live here silently hid 07 Intellectual
                        Property and 08 Private Client, so their subtypes
                        could never be selected and their CCL templates could
                        never be reached. practiceAreas comes from the same
                        /forf/taxonomy payload that drives the cascading
                        specific-matter-type dropdown below. */}
                    {practiceAreas.map((pa) => (
                      <SelectItem
                        key={pa.code}
                        value={pa.code}
                        data-testid={`forf-matter-type-${pa.code.replace(/\s+/g, "-")}`}
                      >
                        {pa.code} — {pa.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Director's guarantee (approved spec, 1 Sep 2026). Where the
                  client is a body corporate the firm requires a director,
                  member or designated member to guarantee the client's fees
                  personally. The name captured here is carried into
                  onboarding and into the GUARANTOR_NAME merge field on the
                  client care letter. Without it the corporate paragraph does
                  not render and the matter is flagged incomplete. */}
              {["company", "llp"].includes((section1.client_type || "").toLowerCase()) && (
                <div className="md:col-span-2" data-testid="forf-guarantor-block">
                  <EditableTextField
                    label="Guarantor (director, member or designated member) *"
                    fieldKey="guarantor_name"
                    testid="forf-guarantor-name-input"
                    value={section1.guarantor_name || ""}
                    onChange={(v) => update1("guarantor_name", v)}
                  />
                  <p className="text-xs text-slate-400 mt-1" data-testid="forf-guarantor-help">
                    This individual signs the guarantee at Schedule 1 to the Terms of Business in their own name.
                    It creates a personal liability for the client's fees. They should be advised to take
                    independent legal advice before signing.
                  </p>
                </div>
              )}

              <div data-testid="prefill-specific-matter-type">
                <Label className="text-xs text-slate-500">
                  Specific matter type *<Tip text={TOOLTIPS.specific_matter_type} id="specific_matter_type" />
                </Label>
                <Select
                  value={section1.specific_matter_type || ""}
                  onValueChange={(v) => update1("specific_matter_type", v)}
                  disabled={!section1.matter_type}
                >
                  <SelectTrigger className="mt-1" data-testid="forf-specific-matter-type-select">
                    <SelectValue placeholder={
                      section1.matter_type ? "Select specific matter type" : "Pick a practice area first"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {currentSpecifics.map((s) => (
                      <SelectItem key={s.code} value={s.code}
                        data-testid={`forf-specific-matter-type-${s.code}`}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Brief 90 Deliverable D: suppress the "Other side" mirror
                  entirely on contentious matters (L5) — the dispute
                  opponent is captured once below in the matter-specific
                  section, so the top mirror would only confuse. On
                  non-contentious matters, render the stripped,
                  descriptor-formatted block from the server (L4); hide
                  when empty so no "Not set" dead box appears. */}
              {section1.matter_type !== "disputes"
                && section1.is_contentious !== true
                && (section1.other_side_block || "").trim() !== "" && (
                  <FieldRow
                    label="Other side"
                    value={section1.other_side_block}
                    testid="prefill-other-side"
                  />
                )}

              {/* 29Apr2026 cleanup pt2 (Vanessa): ToB accepted + ToB version
                  removed from the FORF surface. Terms-of-Business acceptance
                  is captured on the OB (Onboarding) form and is owned by the
                  OB record. The FORF PDF still surfaces the ToB status, but
                  it now reads directly from the OB record at render time. */}
            </div>

            {/* Pro-forma + scope */}
            <h3 className="text-sm font-semibold text-teal mt-6 mb-2">Scope</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <Label className="text-xs text-slate-500">
                  Pro-forma scope *<Tip text={TOOLTIPS.pro_forma_scope} id="pro_forma_scope" />
                </Label>
                <Select
                  value={section1.pro_forma_scope || ""}
                  onValueChange={(v) => {
                    update1("pro_forma_scope", v);
                    // Toggling to "Yes" hides the bespoke scope free-text:
                    // clear any prior bespoke text so we never persist a
                    // stale 1000-char draft alongside the pro-forma flag.
                    if (v === "Yes") {
                      update1("scope_of_work", "");
                    }
                  }}
                >
                  <SelectTrigger className="mt-1" data-testid="forf-pro-forma-scope-select">
                    <SelectValue placeholder="Yes / No" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">
                  Scope exclusions? *<Tip text={TOOLTIPS.scope_exclusions} id="scope_exclusions" />
                </Label>
                <Select
                  value={section1.scope_exclusions || ""}
                  onValueChange={(v) => update1("scope_exclusions", v)}
                >
                  <SelectTrigger className="mt-1" data-testid="forf-scope-exclusions-select">
                    <SelectValue placeholder="Yes / No" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* 29Apr2026 (Vanessa): when pro_forma_scope=Yes the
                  pre-canned pro-forma clause is the scope. The free-text
                  scope-of-work field must NOT be required for submission
                  in that case (this used to block submit demanding a
                  duplicate scope). When pro_forma_scope=No (bespoke), the
                  free-text scope is required, 50-1000 chars. */}
              {section1.pro_forma_scope === "Yes" ? (
                <div className="md:col-span-2" data-testid="forf-scope-of-work-pro-forma-readonly">
                  <Label className="text-xs text-slate-500">
                    Scope of work
                    <span className="text-slate-400 ml-1">(pro-forma template)</span>
                  </Label>
                  <div
                    className="mt-1 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600"
                  >
                    The pro-forma scope-of-work clause for this matter type
                    will be used in the CCL. No free-text entry is required.
                  </div>
                </div>
              ) : (
                <div className="md:col-span-2">
                  <Label className="text-xs text-slate-500">
                    Scope of work{section1.pro_forma_scope === "No" && <span className="text-red-500"> *</span>}
                    <Tip text={TOOLTIPS.scope_of_work} id="scope_of_work" />
                  </Label>
                  <textarea
                    value={section1.scope_of_work || ""}
                    onChange={(e) => update1("scope_of_work", e.target.value)}
                    placeholder="Describe the scope of work (50-1000 chars)"
                    rows={4}
                    maxLength={1000}
                    className="w-full px-3 py-2 mt-1 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal"
                    data-testid="forf-scope-of-work-textarea"
                  />
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {(section1.scope_of_work || "").length}/1000 chars
                    {section1.pro_forma_scope === "No" ? " (min 50)" : ""}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Example: "Advising on and drafting a shareholders agreement for X Ltd, including buyback provisions and pre-emption rights on share transfers."
                  </p>
                </div>
              )}
              {section1.scope_exclusions === "Yes" && (
                <div className="md:col-span-2">
                  <Label className="text-xs text-slate-500">Scope exclusions detail *</Label>
                  <textarea
                    value={section1.scope_exclusions_text || ""}
                    onChange={(e) => update1("scope_exclusions_text", e.target.value)}
                    placeholder="What is outside the retainer (20-1000 chars)"
                    rows={3}
                    maxLength={1000}
                    className="w-full px-3 py-2 mt-1 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal"
                    data-testid="forf-scope-exclusions-text-textarea"
                  />
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {(section1.scope_exclusions_text || "").length}/1000 (min 20)
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Examples: tax advice; employment matters; disputes; cross-border issues; SDLT advice.
                  </p>
                </div>
              )}
            </div>

            {/* PR G (20-May-2026): Letter of Authority capture.
                Vanessa: "ask the client if they need a letter of authority
                prepared and, if yes, basic details to enable admin to
                prepare". Captured here on S1 so admin can act on the
                submitted FoRF.pdf; Section 2 surfaces a read-only
                summary line for the fee earner during risk review. */}
            <h3 className="text-sm font-semibold text-teal mt-6 mb-2">Letter of Authority</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div data-testid="forf-loa-required">
                <Label className="text-xs text-slate-500">
                  Does the client need a Letter of Authority prepared?
                </Label>
                <div className="mt-1 flex gap-4">
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="radio"
                      name="loa_required"
                      value="yes"
                      data-testid="forf-loa-required-yes"
                      checked={section1.loa_required === "yes"}
                      onChange={() => update1("loa_required", "yes")}
                    />
                    Yes
                  </label>
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="radio"
                      name="loa_required"
                      value="no"
                      data-testid="forf-loa-required-no"
                      checked={section1.loa_required === "no"}
                      onChange={() => update1("loa_required", "no")}
                    />
                    No
                  </label>
                </div>
              </div>
              {section1.loa_required === "yes" && (
                <>
                  <div className="md:col-span-2">
                    <EditableTextField
                      label="Who is authorising on the client's behalf?"
                      fieldKey="loa_authorising_parties"
                      testid="forf-loa-authorising-parties-input"
                      value={section1.loa_authorising_parties || ""}
                      onChange={(v) => update1("loa_authorising_parties", v)}
                      tooltip="Party or parties who will instruct via the person instructing us — e.g. one director speaking for the board."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <EditableTextField
                      label="Nature of the authority granted"
                      fieldKey="loa_nature"
                      testid="forf-loa-nature-input"
                      value={section1.loa_nature || ""}
                      onChange={(v) => update1("loa_nature", v)}
                      tooltip="What the authorised person is permitted to instruct on (scope of the authority)."
                    />
                  </div>
                </>
              )}
            </div>

            {/* Commercial terms */}
            <h3 className="text-sm font-semibold text-teal mt-6 mb-2">Commercial terms</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <Label className="text-xs text-slate-500">
                  Pricing model *<Tip text={TOOLTIPS.Q13} id="Q13" />
                </Label>
                <Select value={section1.Q13 || ""} onValueChange={(v) => update1("Q13", v)}>
                  <SelectTrigger className="mt-1" data-testid="forf-Q13-select">
                    <SelectValue placeholder="Fixed fee / Hourly / Blended" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRICING_MODELS.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`forf-Q13-${p.replace(/\s/g, '-')}`}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <EditableTextField
                  label="Fee quote" required fieldKey="Q14" testid="forf-Q14-input"
                  tooltip={TOOLTIPS.Q14}
                  value={section1.Q14 || ""}
                  onChange={(v) => update1("Q14", v)}
                  placeholder="£2500 or 2500-3500"
                  maxLength={50}
                />
                {/* BM-H3332: the fee came from the quote, so say so; it stays editable. */}
                {(prefill?.field_sources || {}).Q14 === "quote" &&
                  section1.Q14 === prefill?.Q14 && (
                  <p className="text-xs text-slate-500 mt-1" data-testid="forf-Q14-from-quote">
                    From the quote. Change it if the fee has moved.
                  </p>
                )}
              </div>
              {/* Fee split (Q15) was dropped from the FORF surface in the
                  29Apr2026 cleanup PR. Per spec the FORF surface does not
                  collect fee-split data; downstream consumers of fee-split
                  data outside the FORF surface are unchanged. */}
              <EditableTextField
                label="Disbursements estimate" required fieldKey="Q16" testid="forf-Q16-input"
                tooltip={TOOLTIPS.Q16}
                value={section1.Q16 || ""}
                onChange={(v) => update1("Q16", v)}
                placeholder="e.g. £500 (Land Registry + searches)"
                maxLength={200}
              />
              <div>
                <Label className="text-xs text-slate-500">
                  Payment on account required? *<Tip text={TOOLTIPS.Q17} id="Q17" />
                </Label>
                <Select value={section1.Q17 || ""} onValueChange={(v) => update1("Q17", v)}>
                  <SelectTrigger className="mt-1" data-testid="forf-Q17-select">
                    <SelectValue placeholder="Yes / No" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {section1.Q17 === "Yes" && (
                <EditableTextField
                  label="Payment on account amount (legal fees, before VAT)" required fieldKey="Q17_amount"
                  testid="forf-Q17-amount-input"
                  value={section1.Q17_amount || ""}
                  onChange={(v) => update1("Q17_amount", v)}
                  placeholder="£500"
                  maxLength={50}
                />
              )}
            </div>

            {/* Per-matter-type conditional fields */}
            {requiredFields.length > 0 && (
              <>
                <h3 className="text-sm font-semibold text-teal mt-6 mb-2">
                  Matter-specific fields ({currentSpecificEntry?.label})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-testid="forf-matter-specific-fields">
                  {requiredFields.map((f) => (
                    <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                      <Label className="text-xs text-slate-500">
                        {f.label}{f.required !== false && <span className="text-red-500"> *</span>}
                      </Label>
                      {f.type === "textarea" ? (
                        <Textarea
                          value={matterSpecific[f.key] || ""}
                          onChange={(e) => updateMS(f.key, e.target.value)}
                          rows={3}
                          maxLength={f.max_length || 1000}
                          className="mt-1"
                          data-testid={`forf-ms-${f.key}`}
                        />
                      ) : f.type === "select" ? (
                        // BM-H87: honour per-field options from the backend
                        // schema. Falls back to the legacy urgency triple
                        // only when the field declares no options (kept for
                        // backwards compatibility with request_new urgency).
                        <Select
                          value={matterSpecific[f.key] || ""}
                          onValueChange={(v) => updateMS(f.key, v)}
                        >
                          <SelectTrigger className="mt-1" data-testid={`forf-ms-${f.key}`}>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(Array.isArray(f.options) && f.options.length > 0
                              ? f.options
                              : ["Standard", "Urgent", "Critical"]
                            ).map((opt) => (
                              <SelectItem key={opt} value={opt}>
                                {opt
                                  .replace(/_/g, " ")
                                  .replace(/\b\w/g, (c) => c.toUpperCase())}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={matterSpecific[f.key] || ""}
                          onChange={(e) => updateMS(f.key, e.target.value)}
                          maxLength={f.max_length || 200}
                          className="mt-1"
                          data-testid={`forf-ms-${f.key}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <Button
                onClick={submitSection1}
                disabled={submitting}
                className="bg-teal hover:bg-teal/90 text-white"
                data-testid="forf-section-1-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Submit FORF section 1
              </Button>
            </div>
          </section>
        )}

        {/* ─────── Section 2 ─────── */}
        {step === "section-2" && (
          <section className="bg-white border border-teal-border rounded-md p-6 shadow-sm" data-testid="forf-section-2">
            <h2 className="text-base font-semibold text-teal mb-1">Section 2 — Risk Assessment</h2>
            <p className="text-xs text-slate-500 mb-4">
              The client and matter risk assessment. The band is worked out
              from your answers. This is the only place the risk assessment
              is completed.
            </p>

            {err && (
              <div className="mb-4 p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-700" data-testid="forf-section-2-error">
                {err}
              </div>
            )}

            {/* PR G (20-May-2026): read-only summary of the LoA answer
                captured in Section 1. Surfaces to the fee earner during
                risk review so they spot a missing LoA brief before sign-off. */}
            {section1?.loa_required && (
              <div className="mb-4 p-3 rounded-md border border-slate-200 bg-slate-50 text-sm" data-testid="forf-loa-summary">
                <span className="font-semibold text-teal">Letter of Authority: </span>
                {section1.loa_required === "yes" ? (
                  <span>
                    Yes — authorising: {section1.loa_authorising_parties || "—"};
                    nature: {section1.loa_nature || "—"}
                  </span>
                ) : (
                  <span>No</span>
                )}
              </div>
            )}

            {onFile && !redoMode ? (
              <div className="p-4 rounded-md border border-teal-border bg-teal-dim/30" data-testid="forf-ra-on-file">
                <h3 className="text-sm font-semibold text-teal mb-1">Risk assessment already on file</h3>
                <p className="text-sm text-slate-700" data-testid="forf-ra-on-file-summary">
                  Band: <span className="font-semibold">{onFile.band || "Not recorded"}</span>
                  {" · "}Date: {onFile.assessed_at ? String(onFile.assessed_at).slice(0, 10) : "Not recorded"}
                  {" · "}By: {onFile.assessed_by || "Not recorded"}
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  Keep this assessment and carry on to the client care letter, or redo it.
                  Redoing adds a new assessment to the history; the old one stays on record.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button
                    onClick={continueWithOnFile}
                    disabled={submitting}
                    className="bg-teal hover:bg-teal/90 text-white"
                    data-testid="forf-ra-on-file-continue"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                    Continue to CCL
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setRedoMode(true); setErr(""); }}
                    disabled={submitting}
                    data-testid="forf-ra-on-file-redo"
                  >
                    Redo risk assessment
                  </Button>
                </div>
              </div>
            ) : (
            <>
            <CmraQuestions
              matterRef={matterRef}
              value={cmra}
              onChange={onCmraChange}
              submitAttempted={cmraSubmitAttempted}
              currentUserEmail={getUserEmail()}
              submissionId={null}
            />

            <h3 className="text-sm font-semibold text-teal mt-6 mb-2">Retainer details</h3>
            <div className="space-y-5 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ra-deadline">Key deadline</Label>
                  <Input
                    id="ra-deadline"
                    value={raData.key_deadline}
                    onChange={(e) => setRaData((d) => ({ ...d, key_deadline: e.target.value }))}
                    data-testid="ra-key-deadline-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ra-review-date">Review date *</Label>
                  <Input
                    id="ra-review-date"
                    type="date"
                    value={raData.review_date}
                    onChange={(e) => setRaData((d) => ({ ...d, review_date: e.target.value }))}
                    data-testid="ra-review-date-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="ra-material">Material risks</Label>
                <Textarea
                  id="ra-material"
                  rows={3}
                  value={raData.material_risks}
                  onChange={(e) => setRaData((d) => ({ ...d, material_risks: e.target.value }))}
                  data-testid="ra-material-risks-input"
                />
              </div>

              <div>
                <Label htmlFor="ra-supervisor">Supervisor notes</Label>
                <Textarea
                  id="ra-supervisor"
                  rows={2}
                  value={raData.supervisor_notes}
                  onChange={(e) => setRaData((d) => ({ ...d, supervisor_notes: e.target.value }))}
                  data-testid="ra-supervisor-notes-input"
                />
              </div>
            </div>
            </>
            )}

            <div className="mt-6 flex justify-between gap-3">
              <Button variant="outline" onClick={() => setStep("section-1")} disabled={submitting} data-testid="forf-section-2-back">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to section 1
              </Button>
              {!(onFile && !redoMode) && (
              <Button
                onClick={submitSection2}
                disabled={submitting}
                className="bg-teal hover:bg-teal/90 text-white"
                data-testid="forf-section-2-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                Submit Risk Assessment
              </Button>
              )}
            </div>
          </section>
        )}

        {/* ─────── Done ─────── */}
        {step === "done" && (
          <section className="bg-white border border-teal-border rounded-md p-6 shadow-sm text-center" data-testid="forf-done">
            <div className="w-12 h-12 rounded-full bg-teal-dim flex items-center justify-center mx-auto mb-3">
              <Check className="w-6 h-6 text-teal" />
            </div>
            <h2 className="text-base font-semibold text-teal mb-1">FORF workflow complete</h2>
            <p className="text-sm text-slate-500 mb-4">
              The FORF PDF and Risk Assessment.pdf have been filed to Compliance & Financials.
              The matter has advanced to CCL Draft.
            </p>
            {mlcoRouting && ["routed", "already_queued"].includes(mlcoRouting.status) && (
              <div className="mb-4 p-3 rounded-md border border-sky-300 bg-sky-50 text-sm text-sky-900 text-left"
                   data-testid="forf-done-mlco-routed">
                This matter is rated {mlcoRouting.band || "high"} risk and has been sent to the
                MLCO for review. It is in the MLCO Inbox, waiting for you to record the action taken.
              </div>
            )}
            {mlcoRouting && mlcoRouting.status === "failed" && (
              <div className="mb-4 p-3 rounded-md border border-red-300 bg-red-50 text-sm text-red-800 text-left"
                   data-testid="forf-done-mlco-failed">
                This matter is rated {mlcoRouting.band || "high"} risk, but it could not be sent to
                the MLCO: {String(mlcoRouting.error || "unknown error")}. Tell the MLCO directly.
              </div>
            )}
            {pipelineWarning && (
              <div className="mb-4 p-3 rounded-md border border-amber-300 bg-amber-50 text-sm text-amber-800 text-left"
                   data-testid="forf-done-pipeline-warning">
                The risk assessment is saved, but the matter pipeline did not move on
                automatically: {String(pipelineWarning)}. You can still open the draft CCL.
              </div>
            )}
            <div className="mt-6 flex justify-center gap-3">
              <Button
                onClick={openDraftCcl}
                disabled={openingCcl}
                className="bg-teal hover:bg-teal/90 text-white"
                data-testid="forf-done-open-ccl"
              >
                {openingCcl ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Open draft CCL
              </Button>
              <Button onClick={() => navigate("/")} variant="outline" data-testid="forf-done-dashboard">
                Back to dashboard
              </Button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
